import React, { memo, useState } from 'react';
import { View, Text, TouchableOpacity, Pressable, Platform } from 'react-native';
import { impactMedium } from '@/utils/haptic';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import { StyleSheet } from 'react-native-unistyles';
import { Tooltip } from '@/components/General/Tooltip';
import Unicon from '@/components/General/Unicon';
import { useRouter } from 'expo-router';
import { getAccessibleTextColor } from '@/utils/colorAccessibility';

/**
 * A component that represents a day in a calendar.
 * @param {object} props - The props for the component.
 * @param {Date} props.date - The date to display.
 * @param {boolean} [props.showTriangle=false] - Whether to show a triangle.
 * @param {boolean} [props.isSelected=false] - Whether the day is selected.
 * @param {string[]} [props.dotColors=[]] - The colors of the dots.
 * @param {Array} [props.events=[]] - The events for this day (used for desktop bars).
 * @param {object} props.size - The size of the day block on the calendar UI.
 * @param {number} props.size.width - The width of the day block on the calendar UI.
 * @param {number} props.size.height - The height of the day block on the calendar UI.
 * @param {Function} props.onPress - The function to call when the day is pressed.
 * @param {Function} props.onLongPress - The function to call when the day is long-pressed.
 * @param {Function} [props.onDoubleTap] - The function to call when the day is double-tapped.
 * @param {number} props.weekdayIndex - The index of the weekday.
 * @param {string} [props.timezone] - The organization's timezone for time display.
 * @returns {React.JSX.Element} The day component.
 */
const Day = ({date, disabled = false, smallCalendar, showTriangle = false, blackoutType = 'none', blackoutCount = 0, isSelected, dotColors, events = [], size, onPress, onLongPress, onDoubleTap = () => {}, weekdayIndex, canCreateEvents = false, timezone, onEventPress}) => {
    const isToday = !date ? false : new Date().toDateString() == date.toDateString();
    const [isHovered, setIsHovered] = useState(false);
    const router = useRouter();
    const isWeb = Platform.OS === 'web';

    const dots = date ? dotColors?.map(color => {
        return <View key={color + date.toDateString()} style={styles.dot(color)}></View>
    }) : []

    const barHeightEstimate = isWeb && !smallCalendar ? 22 : 18;
    const maxBarsToShow = Math.max(1, Math.floor((size.height - 40) / barHeightEstimate));
    const visibleEvents = events?.slice(0, maxBarsToShow) || [];
    const remainingCount = (events?.length || 0) - maxBarsToShow;

    const handleEventBarPress = (event) => {
        if (event.noPress) return;
        if (onEventPress) {
            onEventPress(event);
            return;
        }
        if (event.id && !event.id.startsWith('holiday_') && !event.id.startsWith('blackout_')) {
            router.push({ pathname: "/events/instances/" + event.id });
        }
    };

    const eventBars = date && !smallCalendar ? visibleEvents.map((event, index) => {
        const isAllDay = event.allDay === true;
        const hasTime = !isAllDay && event.startTime != null;
        const timeStr = hasTime ? new Date(event.startTime).toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: timezone }) : '';
        const tooltipTitle = event.name;
        const tooltipSubtitle = `${isAllDay ? 'All Day' : timeStr}${event.location ? ' • ' + event.location : ''}`;
        const isBlackout = event.id && event.id.startsWith('blackout_');

        const bar = (
            <View style={styles.eventBar(event.color)}>
                <Text numberOfLines={1} style={styles.eventBarText(event.color)}>{timeStr ? timeStr + ' ' : ''}{event.name}</Text>
            </View>
        );

        if (isBlackout) {
            return <View key={event.id || index}>{bar}</View>;
        }

        return (
            <Tooltip
                testID={`EventBar-${event.name}`}
                key={event.id || index}
                title={tooltipTitle}
                subtitle={tooltipSubtitle}
                onPress={() => handleEventBarPress(event)}
            >
                {bar}
            </Tooltip>
        );
    }) : null

    const longPress = () => {
        if(isWeb) return;

        impactMedium();
        onLongPress(date);
    }

    const doubleTap = () => {
        if(isWeb) return;

        impactMedium();
        onDoubleTap(date);
    }



    const DayContent = (
        <View style={styles.innerContainer(isSelected, isToday, smallCalendar, disabled)}>
            <View style={styles.dateRow(smallCalendar)}>
                <Text style={styles.dateText(isSelected, smallCalendar)}>{date?.getDate()}</Text>
                {/* Desktop hover action icons */}
                {!smallCalendar && isWeb && canCreateEvents && (
                    <View style={styles.hoverActions(isHovered)}>
                        <Tooltip title="Add a new Event" testID="day-menu-new-event" onPress={() => onLongPress(date)}>
                            <View style={styles.hoverActionButton(disabled)}>
                                <Unicon name="add-outline" size={14} colorVariant="primary" />
                            </View>
                        </Tooltip>
                        <Tooltip title="Add an occurrence of an existing Event" testID="day-menu-add-occurrence" onPress={() => onDoubleTap(date)}>
                            <View style={styles.hoverActionButton(disabled)}>
                                <Unicon name="calendar-blank-multiple" library='material' size={14} colorVariant="primary" />
                            </View>
                        </Tooltip>
                        <Tooltip title="Add a Blackout" testID="day-menu-add-blackout" onPress={() => router.push({ pathname: "/events/blackouts/create", params: { selectedDate: date.toISOString() } })}>
                            <View style={styles.hoverActionButton(disabled)}>
                                <Unicon name="ban-outline" size={14} colorVariant="primary" />
                            </View>
                        </Tooltip>
                    </View>
                )}
            </View>
            { smallCalendar ? (
                <View style={styles.dots(smallCalendar)}>
                    {dots}
                </View>
            ) : (
                <View style={styles.eventBarsContainer}>
                    {eventBars}
                    {remainingCount > 0 && (
                        <Text style={styles.moreEventsText}>+{remainingCount} more</Text>
                    )}
                </View>
            )}
        </View>
    );

    if (!date) {
        return (
            <View style={styles.container(size, weekdayIndex, smallCalendar, true)}>
                <View style={styles.emptyDay}></View>
            </View>
        );
    }

    // On desktop (web), disable gestures and use hover icons instead
    if (isWeb && !smallCalendar) {
        return (
            <View
                style={{ zIndex: isHovered ? 10 : 0 }}
                onPointerEnter={() => setIsHovered(true)}
                onPointerLeave={() => setIsHovered(false)}
            >
                <Pressable
                    testID={`Day-${date.getDate()}`}
                    style={styles.container(size, weekdayIndex, smallCalendar, disabled)}
                    onPress={() => onPress(date)}
                >
                    {DayContent}
                    {(showTriangle || blackoutType !== 'none') && <BlackoutOverlay count={blackoutCount} />}
                </Pressable>
            </View>
        );
    }

    // On mobile/native, use gestures
    return (
        <GestureDetector gesture={Gesture.Tap().numberOfTaps(2).runOnJS(true).onStart(() => doubleTap())}>
            <TouchableOpacity
                disabled={disabled}
                testID={`Day-${date.getDate()}`} 
                style={styles.container(size, weekdayIndex, smallCalendar, disabled)} 
                onLongPress={longPress} 
                onPress={() => onPress(date)}
            >
                {(showTriangle || blackoutType !== 'none') && <BlackoutOverlay count={blackoutCount} />}
                {DayContent}
            </TouchableOpacity>
        </GestureDetector>
    );
}

const BlackoutOverlay = ({count}) => (
    <Tooltip style={blackoutStyles.container} title={count > 0 ? `${count} Blackout${count !== 1 ? 's' : ''} on this day` : 'Blackout day'}>
        <View style={blackoutStyles.triangle} />
    </Tooltip>
);

const blackoutStyles = StyleSheet.create(({colors}) => ({
    container: {
        position: 'absolute',
        top: 0,
        right: 0,
        zIndex: 100,
        pointerEvents: 'none',
        width: 0,
        height: 0,
        overflow: 'visible',
    },
    triangle: {
        borderTopColor: colors.events,
        position: 'absolute',
        top: 0,
        right: 0,
        width: 0,
        height: 0,
        borderStyle: 'solid',
        borderTopWidth: 16,
        borderLeftWidth: 16,
        borderLeftColor: 'transparent',
    },
}));

const styles = StyleSheet.create(({colors, fonts}, rt) => ({
    container: (size, weekdayIndex, smallCalendar, noDate) => ({
        width:size.width, 
        height:size.height, 
        padding:!smallCalendar ? 0 : 5, 
        borderTopWidth:!smallCalendar ? 1 : 0, 
        borderLeftWidth:!smallCalendar && weekdayIndex > 0 ? 1 : 0, 
        borderColor:colors.border,
        _web:{
            _hover:{
                backgroundColor:noDate ? "transparent" : colors.hover,
                borderRadius:!smallCalendar ? 0 : 8
            }
        }
    }),
    emptyDay: {
        backgroundColor: "transparent",
        width: "100%",
        height: "100%"
    },
    innerContainer: (isSelected, isToday, smallCalendar, disabled) => ({
        backgroundColor:isSelected ? colors.primaryContainer : (isToday ? colors.primaryContainer : "transparent"),
        width:"100%",
        height:"100%",
        borderRadius:!smallCalendar ? 0 : 8,
        alignItems:!smallCalendar ? "flex-start" : "center",
        paddingTop:3,
        opacity: disabled ? 0.5 : 1,
    }),
    dateRow: (smallCalendar) => ({
        flexDirection: 'row',
        justifyContent: smallCalendar ? 'center' : 'space-between',
        alignItems: 'center',
        width: '100%',
        paddingRight: smallCalendar ? 0 : 4
    }),
    dateText: (isSelected, smallCalendar) => ({
        padding:!smallCalendar ? 4 : 0,
        paddingLeft:!smallCalendar ? 8 : 0,
        color:isSelected ? colors.primary : colors.text,
        fontSize:(!smallCalendar ? 18 : 16) * (0.5 + 0.5 * rt.fontScale),
        fontFamily:fonts.bold,
        textAlign: smallCalendar ? 'center' : 'left'
    }),
    hoverActions: (isVisible) => ({
        flexDirection: 'row',
        gap: 4,
        paddingRight: 4,
        opacity: isVisible ? 1 : 0,
        pointerEvents: isVisible ? 'auto' : 'none'
    }),
    hoverActionButton: (disabled) => ({
        width: 22,
        height: 22,
        borderRadius: 4,
        alignItems: 'center',
        justifyContent: 'center',
        _web: {
            cursor: 'pointer',
            _hover: {
                backgroundColor: disabled ? "transparent" : colors.primaryContainer
            }
        },
    }),
    dots: (smallCalendar) => ({
        flexDirection:"row", 
        justifyContent:!smallCalendar ? "flex-start" : "center", 
        width:"100%", 
        alignItems:!smallCalendar ? "flex-start" : "center", 
        flexWrap:"wrap", 
        paddingHorizontal:8
    }),
    dot: (color) => ({
        backgroundColor: color || 'transparent', 
        height:4, 
        width:4, 
        borderRadius:4, 
        margin:1
    }),
    eventBarsContainer: {
        flex: 1,
        width: '100%',
        paddingHorizontal: 2,
        paddingTop: 2,
        gap: 2,
        overflow: 'hidden'
    },
    eventBar: (color) => ({
        backgroundColor: color || colors.primary,
        borderRadius: 3,
        paddingHorizontal: 4,
        paddingVertical: 1,
        width: '100%',
        _web: {
            paddingVertical: 3,
        }
    }),
    eventBarText: (backgroundColor) => ({
        color: getAccessibleTextColor(backgroundColor || colors.primary),
        fontSize: 10,
        fontFamily: fonts.body,
        _web: {
            fontSize: 11,
        }
    }),
    moreEventsText: {
        color: colors.textSecondary,
        fontSize: 9,
        paddingHorizontal: 4,
        fontFamily: fonts.body
    },
}))

export default memo(Day)